import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { AuthorizedRequestArg } from '../signin/auth';
import * as authFetch from '../signin/authFetch';
import { GetEntriesResponse } from '../timeseries/timeseriesSlice';
import { listGoals } from './goalsSlice';
import { Goal, GoalUpdate } from './types';

interface GoalState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  data: GetGoalResponse | null;
};

interface DeleteGoalArg extends AuthorizedRequestArg {
  goalID: string;
};

interface DeleteGoalRequest {
  deleteGoal: DeleteGoalArg;
};

interface DeleteGoalResponse {
};

export const deleteGoal = createAsyncThunk('goals/deleteGoal', async (arg: DeleteGoalArg, { rejectWithValue, dispatch }) => {
  const response = await authFetch.post<DeleteGoalRequest, DeleteGoalResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'deleteGoal': arg,
    },
    arg.authToken,
    dispatch,
  );
  dispatch(listGoals({ authToken: arg.authToken }));
  if (response.error) {
    return rejectWithValue(response.error);
  }
  return response.data || null;
});

interface GetGoalArg extends AuthorizedRequestArg {
  goalID: string;
  includeEntries: boolean;
};

interface GetGoalRequest {
  getGoal: GetGoalArg;
};

interface GetGoalResponse {
  goal: Goal;
  entries: GetEntriesResponse;
};

export const getGoal = createAsyncThunk('goals/getGoal', async (arg: GetGoalArg, { rejectWithValue, dispatch }) => {
  const response = await authFetch.post<GetGoalRequest, GetGoalResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'getGoal': arg,
    },
    arg.authToken,
    dispatch,
  );
  if (response.error) {
    return rejectWithValue(response.error);
  }
  return response.data || null;
});

interface WriteGoalArg extends AuthorizedRequestArg {
  goal: GoalUpdate;
};

interface WriteGoalRequest {
  writeGoal: WriteGoalArg;
};

interface WriteGoalResponse {
  goal: Goal;
};

export const writeGoal = createAsyncThunk('goals/writeGoal', async (arg: WriteGoalArg, { dispatch }) => {
  const response = await authFetch.post<WriteGoalRequest, WriteGoalResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'writeGoal': arg,
    },
    arg.authToken,
    dispatch
  );
  dispatch(listGoals({ authToken: arg.authToken }));
  return response.data || null;
});

export const goalSlice = createSlice({
  name: 'goals',
  initialState: {
    status: 'idle',
    error: null,
    data: null,
  } as GoalState,
  reducers: {
  },
  extraReducers: builder => {
    builder.addCase(deleteGoal.pending, (state, action) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(deleteGoal.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.error = null;
      state.data = null;
    });
    builder.addCase(deleteGoal.rejected, (state, action) => {
      console.log('Error', action);
      state.data = null;
      state.status = 'failed';
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(getGoal.pending, (state, action) => {
      state.status = 'loading';
      state.error = null;
      // Clear out state if we're loading a new goal.
      if (state.data?.goal.id !== action.meta.arg.goalID) {
        state.data = null;
      }
    });
    builder.addCase(getGoal.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.error = null;
      state.data = action.payload;
    });
    builder.addCase(getGoal.rejected, (state, action) => {
      console.log('Error', action);
      state.status = 'failed';
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });

    builder.addCase(writeGoal.pending, (state, action) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(writeGoal.fulfilled, (state, action) => {
      console.log('Success', action.payload);
      state.status = 'succeeded';
      state.error = null;
    });
    builder.addCase(writeGoal.rejected, (state, action) => {
      console.log('Error', action.error.message)
      state.status = 'failed';
      if (action.payload) {
        state.error = action.payload as string;
      } else {
        state.error = action.error.message || null;
      }
    });
  }
});

export const selectGoal = (state: RootState) => state.goal.data?.goal || null;

export const selectEntries = (state: RootState) => state.goal.data?.entries || { series: [] };

export default goalSlice.reducer